/**
 * UI State Manager - Handles loading states, empty states, and UI utility functions
 * Part of the SOLID Course Materials System
 *
 * @author Course Materials Team
 * @version 1.0.0
 */

class UIStateManager {
  /**
   * @param {Object} [config] - Configuration object
   * @param {Object} [config.selectors] - DOM selectors configuration
   */
  constructor(config = /** @type {any} */ ({})) {
    // Default DOM selectors - can be customized via config
    this.selectors = {
      loadingState: '#loading-state',
      noProductsFound: '#no-products-found',
      filteredProducts: '#filtered-products',
      adoptionMessage: '.adoption-message',
      ...config.selectors,
    };

    // Bind methods to preserve context
    this.showLoadingState = this.showLoadingState.bind(this);
    this.hideLoadingState = this.hideLoadingState.bind(this);
    this.showEmptyState = this.showEmptyState.bind(this);
    this.showEmptyStateWithAdoption =
      this.showEmptyStateWithAdoption.bind(this);
    this.hideEmptyState = this.hideEmptyState.bind(this);
    this.createInlineLoader = this.createInlineLoader.bind(this);
    this.removeInlineLoader = this.removeInlineLoader.bind(this);
    this.updateSelectors = this.updateSelectors.bind(this);
    this.getCurrentState = this.getCurrentState.bind(this);
    this.dispatchStateChangeEvent = this.dispatchStateChangeEvent.bind(this);
  }

  /**
   * Updates the DOM selectors configuration
   * @param {Object} newSelectors - New selectors configuration
   * @returns {void}
   */
  updateSelectors(newSelectors) {
    this.selectors = { ...this.selectors, ...newSelectors };
  }

  /**
   * Show loading state - no animations
   * @returns {void}
   */
  showLoadingState() {
    const loadingState = document.querySelector(this.selectors.loadingState);
    const noProductsFound = document.querySelector(
      this.selectors.noProductsFound
    );
    const filteredProducts = document.querySelector(
      this.selectors.filteredProducts
    );
    if (loadingState) {
      // Keep full-page loader outside local stacking/overflow contexts.
      if (loadingState.parentElement !== document.body) {
        document.body.appendChild(loadingState);
      }
      loadingState.style.display = 'flex';
    }
    if (noProductsFound) noProductsFound.style.display = 'none';
    //if (filteredProducts) filteredProducts.innerHTML = '';

    this.dispatchStateChangeEvent('loading', 'shown');
    if (typeof window.syncCmrpProductsSectionShell === 'function') {
      window.syncCmrpProductsSectionShell();
    }
  }

  /**
   * Hide loading state
   * @returns {void}
   */
  hideLoadingState() {
    // CMRP: keep full-page #loading-state until getFilteredCampusTerm has merged,
    // populateCampus() has filled campus selects, and deep-link URL restore has
    // finished DDCS metaobject lookups + product queries (avoid overlay flash).
    if (
      typeof document !== 'undefined' &&
      document.body?.classList?.contains('course-materials-page') &&
      (window.__cmrpCampusTermFetchApplied !== true ||
        window.__cmrpHoldLoadingUntilResults === true)
    ) {
      window.__cmrpPendingHideLoadingState = true;
      return;
    }

    const loadingState = document.querySelector(this.selectors.loadingState);
    if (loadingState) loadingState.style.display = 'none';

    this.dispatchStateChangeEvent('loading', 'hidden');
    if (typeof window.syncCmrpProductsSectionShell === 'function') {
      window.syncCmrpProductsSectionShell();
    }
  }

  /**
   * Show empty state without adoption message
   * @returns {void}
   */
  showEmptyState() {
    this.hideLoadingState();
    const noProductsFound = document.querySelector(
      this.selectors.noProductsFound
    );
    const adoptionMessage = document.querySelector(
      this.selectors.adoptionMessage
    );

    if (noProductsFound) noProductsFound.style.display = 'block';
    // Hide adoption message in regular empty state
    if (adoptionMessage) adoptionMessage.style.display = 'none';

    this.dispatchStateChangeEvent('empty', 'shown');
    if (typeof window.syncCmrpProductsSectionShell === 'function') {
      window.syncCmrpProductsSectionShell();
    }
  }

  /**
   * Show empty state with adoption message
   * @returns {void}
   */
  showEmptyStateWithAdoption() {
    this.hideLoadingState();
    const noProductsFound = document.querySelector(
      this.selectors.noProductsFound
    );
    const adoptionMessage = document.querySelector(
      this.selectors.adoptionMessage
    );

    if (noProductsFound) noProductsFound.style.display = 'block';
    // Show adoption message when there's adoption_status
    if (adoptionMessage) {
      adoptionMessage.style.display = 'block';
    }

    this.dispatchStateChangeEvent('empty', 'shown-with-adoption');
    if (typeof window.syncCmrpProductsSectionShell === 'function') {
      window.syncCmrpProductsSectionShell();
    }
  }

  /**
   * Hide empty state
   * @returns {void}
   */
  hideEmptyState() {
    const noProductsFound = document.querySelector(
      this.selectors.noProductsFound
    );
    if (noProductsFound) noProductsFound.style.display = 'none';

    this.dispatchStateChangeEvent('empty', 'hidden');
    if (typeof window.syncCmrpProductsSectionShell === 'function') {
      window.syncCmrpProductsSectionShell();
    }
  }

  /**
   * Utility function for creating loading states anywhere
   * @param {string} containerId - Container element ID
   * @param {Object} [options] - Loader options
   * @param {string} [options.title='Loading...'] - Loader title
   * @param {string} [options.subtitle=''] - Loader subtitle
   * @param {string} [options.size='medium'] - Loader size (small/medium/large)
   * @param {boolean} [options.overlay=false] - Whether to show as overlay
   * @returns {string|null} Loader ID for removal, or null if container not found
   */
  createInlineLoader(containerId, options = {}) {
    const container = document.getElementById(containerId);
    if (!container) return null;

    const {
      title = 'Loading...',
      subtitle = '',
      size = 'medium',
      overlay = false,
    } = options;

    const loaderId = `${containerId}-loader`;
    const loaderClass = overlay
      ? 'loading-state loading-overlay'
      : 'loading-state loading-inline';

    // Size configurations
    const sizeConfig = /** @type {any} */ ({
      small: { spinner: '24px', title: '1rem', subtitle: '0.75rem' },
      medium: { spinner: '40px', title: '1.25rem', subtitle: '0.875rem' },
      large: { spinner: '48px', title: '1.5rem', subtitle: '1rem' },
    });

    const config = sizeConfig[size] || sizeConfig.medium;

    const loaderHTML = `
      <div id="${loaderId}" class="${loaderClass}">
        <div class="loading-content">
          <div class="loading-spinner">
            <div class="spinner" style="width: ${config.spinner}; height: ${
      config.spinner
    };"></div>
          </div>
          <div class="loading-text">
            <h3 class="loading-title" style="font-size: ${
              config.title
            };">${title}</h3>
            ${
              subtitle
                ? `<p class="loading-subtitle" style="font-size: ${config.subtitle};">${subtitle}</p>`
                : ''
            }
          </div>
        </div>
      </div>
    `;

    container.insertAdjacentHTML('beforeend', loaderHTML);

    this.dispatchStateChangeEvent('inline-loader', 'created', {
      loaderId,
      containerId,
      options,
    });

    return loaderId;
  }

  /**
   * Remove inline loader
   * @param {string} loaderId - Loader ID to remove
   * @returns {boolean} True if loader was found and removed
   */
  removeInlineLoader(loaderId) {
    const loader = document.getElementById(loaderId);
    if (loader) {
      loader.remove();
      this.dispatchStateChangeEvent('inline-loader', 'removed', { loaderId });
      return true;
    }
    return false;
  }

  /**
   * Get current UI state information
   * @returns {Object} Current state information
   */
  getCurrentState() {
    const loadingState = document.querySelector(this.selectors.loadingState);
    const noProductsFound = document.querySelector(
      this.selectors.noProductsFound
    );
    const adoptionMessage = document.querySelector(
      this.selectors.adoptionMessage
    );
    const filteredProducts = document.querySelector(
      this.selectors.filteredProducts
    );

    const inlineLoaders = document.querySelectorAll(
      '.loading-state.loading-inline'
    );
    const overlayLoaders = document.querySelectorAll(
      '.loading-state.loading-overlay'
    );

    return {
      loading: {
        visible: loadingState ? loadingState.style.display === 'flex' : false,
        element: loadingState,
      },
      empty: {
        visible: noProductsFound
          ? noProductsFound.style.display === 'block'
          : false,
        withAdoption: adoptionMessage
          ? adoptionMessage.style.display === 'block'
          : false,
        element: noProductsFound,
      },
      products: {
        hasContent: filteredProducts
          ? filteredProducts.innerHTML.trim().length > 0
          : false,
        element: filteredProducts,
      },
      loaders: {
        inline: inlineLoaders.length,
        overlay: overlayLoaders.length,
        total: inlineLoaders.length + overlayLoaders.length,
      },
    };
  }

  /**
   * Show loading state for a specific duration
   * @param {number} duration - Duration in milliseconds
   * @param {Function} [callback] - Callback to execute after duration
   * @returns {Promise<void>} Promise that resolves after duration
   */
  showLoadingForDuration(duration, callback) {
    this.showLoadingState();

    return new Promise(resolve => {
      setTimeout(() => {
        this.hideLoadingState();
        if (callback) callback();
        resolve();
      }, duration);
    });
  }

  /**
   * Create a temporary inline loader that removes itself after duration
   * @param {string} containerId - Container element ID
   * @param {number} duration - Duration in milliseconds
   * @param {Object} [options] - Loader options
   * @returns {Promise<string|null>} Promise resolving to loader ID or null
   */
  async createTemporaryLoader(containerId, duration, options = {}) {
    const loaderId = this.createInlineLoader(containerId, options);
    if (!loaderId) return null;

    return new Promise(resolve => {
      setTimeout(() => {
        this.removeInlineLoader(loaderId);
        resolve(loaderId);
      }, duration);
    });
  }

  /**
   * Clean up all inline loaders
   * @returns {number} Number of loaders removed
   */
  cleanupAllLoaders() {
    const inlineLoaders = document.querySelectorAll(
      '.loading-state.loading-inline'
    );
    const overlayLoaders = document.querySelectorAll(
      '.loading-state.loading-overlay'
    );

    let removedCount = 0;

    [...inlineLoaders, ...overlayLoaders].forEach(
      /** @param {Element} loader */ loader => {
        if (loader.id) {
          this.removeInlineLoader(loader.id);
          removedCount++;
        } else {
          loader.remove();
          removedCount++;
        }
      }
    );

    this.dispatchStateChangeEvent('cleanup', 'completed', { removedCount });

    return removedCount;
  }

  /**
   * Set UI state based on data conditions
   * @param {Object} conditions - Conditions to check
   * @param {boolean} [conditions.hasData] - Whether there is data
   * @param {boolean} [conditions.isLoading] - Whether currently loading
   * @param {boolean} [conditions.hasAdoptionStatus] - Whether adoption status exists
   * @returns {void}
   */
  setStateBasedOnConditions(conditions) {
    const { hasData, isLoading, hasAdoptionStatus } = conditions;

    if (isLoading) {
      this.showLoadingState();
    } else if (hasData) {
      this.hideLoadingState();
      this.hideEmptyState();
    } else {
      this.hideLoadingState();
      if (hasAdoptionStatus) {
        this.showEmptyStateWithAdoption();
      } else {
        this.showEmptyState();
      }
    }
  }

  /**
   * Dispatches a custom event when UI state changes
   * @private
   * @param {string} stateType - Type of state change
   * @param {string} action - Action performed
   * @param {any} [data] - Additional event data
   * @returns {void}
   */
  dispatchStateChangeEvent(stateType, action, data = {}) {
    const event = new CustomEvent('uiState:changed', {
      detail: {
        stateType,
        action,
        data,
        currentState: this.getCurrentState(),
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Initialize UI state manager
   * @returns {void}
   */
  initialize() {
    // Initial state check
    const currentState = this.getCurrentState();
    this.dispatchStateChangeEvent('initialization', 'completed', {
      currentState,
    });
  }

  /**
   * Clean up and destroy the UI state manager
   * @returns {void}
   */
  destroy() {
    // Clean up all loaders
    this.cleanupAllLoaders();

    // Remove global references
    if (typeof window !== 'undefined') {
      delete (/** @type {any} */ (window).courseMaterialsUIStateManager);
    }
  }
}

// Export for use in other modules and global scope
window.UIStateManager = UIStateManager;

// Create namespace if it doesn't exist
if (!window.CourseMatSystem) {
  window.CourseMatSystem = {};
}
window.CourseMatSystem.UIStateManager = UIStateManager;

/**
 * CMRP: `#products-section` is always in the DOM (JS moves it under `.cmrp-results-anchor`).
 * Legacy `.products-section` / `.right-panel-content` padding still paints a gray slab when
 * there is nothing to show. Toggle a compact class when the docked shell is visually idle.
 * Student-id and course-builder share this shell; only landing-chrome (SID off) adds
 * `cmrp-cb-results-active` for full-bleed course-only layout.
 * @returns {void}
 */
function syncCmrpHasResultsClass() {
  const hasCards = !!document.querySelector('.course-card');
  document
    .querySelectorAll('.course-materials, .course-materials-container')
    .forEach(el => {
      el.classList.toggle('cmrp-has-results', hasCards);
    });
}

function syncCmrpProductsSectionShell() {
  syncCmrpHasResultsClass();
  const section = document.getElementById('products-section');
  if (
    !section ||
    typeof document === 'undefined' ||
    !document.body?.classList?.contains('course-materials-page')
  ) {
    return;
  }

  const anchor = section.closest('.cmrp-results-anchor');
  const filtersSection = document.getElementById('filters-section');
  const clearCourseBuilderRenderedClass = () =>
    filtersSection?.classList.remove('cmrp-cb-results-active');

  if (!anchor) {
    section.classList.remove('products-section--cmrp-compact');
    clearCourseBuilderRenderedClass();
    return;
  }

  const loadingEl = document.getElementById('loading-state');
  const loadingVisible =
    loadingEl && getComputedStyle(loadingEl).display !== 'none';

  const grid = document.getElementById('filtered-products');
  const gridEmpty = grid ? grid.children.length === 0 : true;

  const noProducts = document.getElementById('no-products-found');
  let noProductsMeaningful = false;
  if (noProducts && getComputedStyle(noProducts).display !== 'none') {
    noProductsMeaningful = noProducts.offsetHeight > 4;
  }

  const compact =
    gridEmpty && (loadingVisible || !noProductsMeaningful);

  section.classList.toggle('products-section--cmrp-compact', compact);

  const isCourseBuilderAnchor = anchor.classList.contains('cmrp-results-anchor--course-builder');
  const isLandingChrome = filtersSection?.classList.contains('cmrp-filters-panel--landing-chrome') === true;
  const isStudentIdOff = window.shopifySettings?.studentIDActivation === false;
  const shouldMarkCourseBuilderRendered =
    isCourseBuilderAnchor && isLandingChrome && isStudentIdOff && !compact;

  if (shouldMarkCourseBuilderRendered) {
    filtersSection?.classList.add('cmrp-cb-results-active');
  } else {
    clearCourseBuilderRenderedClass();
  }
}

window.syncCmrpProductsSectionShell = syncCmrpProductsSectionShell;

function ensureCmrpProductsSectionShellObserver() {
  if (typeof document === 'undefined') return;
  const grid = document.getElementById('filtered-products');
  if (!grid || grid.dataset.cmrpShellObserved === '1') return;
  grid.dataset.cmrpShellObserved = '1';
  const mo = new MutationObserver(() => syncCmrpProductsSectionShell());
  mo.observe(grid, { childList: true, subtree: true });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      ensureCmrpProductsSectionShellObserver();
      syncCmrpProductsSectionShell();
    });
  } else {
    ensureCmrpProductsSectionShellObserver();
    syncCmrpProductsSectionShell();
  }
  document.addEventListener('product-display:updated', () =>
    syncCmrpProductsSectionShell()
  );
}
